/**
 * `BULK_CHANGE_DATE` step. Single-call wrapper over
 * `POST /api/v1/core/route/stops/change-date/bulk` (`bulkChangeDate`). Its batch shape
 * is its own — `routeId` / `targetDate`, and no `updateMasterSequence`, since the master
 * cascade is implicit BE-side. Atomic: it flips the daily date and the master
 * day-of-week together. Uses `skipInvalidate:true`.
 *
 * Answers 200 even when it moved nothing, listing the stops it refused, so the body is
 * read rather than discarded: an all-refused response fails the step, a partial one
 * records the count.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { BulkChangeDateBody, BulkChangeDateRejection, BulkChangeDateResponse } from 'types/api/route/bulkRequests'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface BulkChangeDateStepArgs {
  readonly body: BulkChangeDateBody
}

export type BulkChangeDateStepValue = { readonly committed: true; readonly skipped: number }

const describeReasons = (rejected: ReadonlyArray<BulkChangeDateRejection>): string => {
  const reasons = Array.from(new Set(rejected.map(entry => entry?.reason).filter(Boolean)))
  return reasons.length > 0 ? reasons.join('; ') : 'the backend gave no reason'
}

export type BulkChangeDateStepResult = StepResult<'BULK_CHANGE_DATE', BulkChangeDateStepValue>

const STEP_ID = 'bulk-change-date'

export const makeBulkChangeDateStep = (
  args: BulkChangeDateStepArgs,
  deps: Deps
): PipelineStep<'BULK_CHANGE_DATE', BulkChangeDateStepResult> => {
  return {
    id: STEP_ID,
    phase: 'BULK_CHANGE_DATE',
    label: 'Failed to apply bulk change-date',
    applyOrder: 200,
    run: async () => {
      if (!deps.bulkChangeDate) {
        return err({
          kind: 'PRECONDITION_FAILED',
          reason: 'bulkChangeDate dep not provided — the v1-bulk strategy requires deps.bulkChangeDate to be wired',
        })
      }
      const submitted = args.body.moves.reduce((total, move) => total + (move.stops?.length ?? 0), 0)
      try {
        const response = (await deps
          .bulkChangeDate({
            ...args.body,
            skipInvalidate: true,
          } as unknown as Parameters<NonNullable<Deps['bulkChangeDate']>>[0])
          .unwrap()) as BulkChangeDateResponse | undefined
        // A 200 does not mean the stops moved — the BE answers with the ones it refused
        // and their reasons. Reading it is the difference between "nothing moved" and a
        // success toast over an unchanged board.
        const rejected = response?.stops ?? []
        if (submitted > 0 && rejected.length >= submitted) {
          return err({
            kind: 'BE_REJECTED',
            phase: 'BULK_CHANGE_DATE',
            status: 200,
            message: `No stops were moved — ${describeReasons(rejected)}`,
          })
        }
        return ok({ phase: 'BULK_CHANGE_DATE', stepId: STEP_ID, value: { committed: true, skipped: rejected.length } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'BULK_CHANGE_DATE',
          status,
          message,
        })
      }
    },
  }
}
